/* eslint-disable brace-style */
import type { Node as ProseMirrorNode } from '@tiptap/pm/model';
import type { Editor, JSONContent } from '@tiptap/react';
import { capitalize } from 'lodash';
import {
  BLOCKQUOTE,
  BULLET_LIST_TYPE,
  CODE_BLOCK_TYPE,
  generateId,
  HEADER_BLOCK,
  NUMBERED_LIST_TYPE,
  RICOS_INDENT_TYPE,
  RICOS_LINE_SPACING_TYPE,
  UNSTYLED,
} from 'ricos-content';
import { fromTiptapNode } from 'ricos-converters';
import type { Node } from 'ricos-schema';
import { Decoration_Type, Node_Type } from 'ricos-schema';
import type { ColorType, EditorCommands, FocusPosition, TiptapAdapter } from 'ricos-types';
import { ToolbarType } from 'ricos-types';
import type { TextAlignment } from 'wix-rich-content-common';
import {
  DOC_STYLE_TYPES,
  RICOS_ANCHOR_TYPE,
  RICOS_FONT_SIZE_TYPE,
  RICOS_LINK_TYPE,
  RICOS_MENTION_TYPE,
  RICOS_TEXT_COLOR_TYPE,
  RICOS_TEXT_HIGHLIGHT_TYPE,
} from 'wix-rich-content-common';
import { resolversById } from 'wix-rich-content-toolbars-v3';
import { RESOLVERS_IDS } from 'wix-rich-content-toolbars-v3/libs/resolvers-ids';

import { TO_TIPTAP_TYPE } from '../consts';
import { findNodeById } from '../helpers';
import { convertInlineStylesToCSS } from '../helpers/convertInlineStylesToCss';
import type { TiptapAdapterServices } from '../initializeTiptapAdapter';
import { isSchemaCompatibleContent } from './is-schema-compatible-content';

// !@todo: this shit is too big, fix it! next-major. Resolvers are also not REACTIVE >:(

export class RichContentAdapter implements TiptapAdapter {
  getToolbarProps: TiptapAdapter['getToolbarProps'];

  private readonly services: TiptapAdapterServices;

  constructor(public tiptapEditor: Editor, services: TiptapAdapterServices) {
    this.tiptapEditor = tiptapEditor;
    this.getEditorCommands = this.getEditorCommands.bind(this);
    this.getToolbarProps = type => {
      const buttons =
        type === ToolbarType.INSERT_PLUGIN
          ? services.plugins.getAddButtons()
          : services.plugins.getTextButtons();
      return {
        buttons: buttons.toExternalToolbarButtonsConfigs(
          this.getEditorCommands(),
          tiptapEditor,
          services.context.isMobile,
          services.getEditorQuery()
        ),
      };
    };
    this.services = services;
  }

  getContainer = () => {
    return this.tiptapEditor?.options?.element;
  };

  getDocumentStyle = () => this.tiptapEditor?.getJSON()?.attrs?.documentStyle || {};

  focus(focusPosition?: FocusPosition) {
    this.tiptapEditor.commands.focus(focusPosition);
  }

  blur() {
    this.tiptapEditor.commands.blur();
  }

  setTiptapContent(content: JSONContent): void {
    if (isSchemaCompatibleContent(content, this.tiptapEditor.schema)) {
      this.tiptapEditor.commands.setContent(content);
      console.debug('New content applied', content); // eslint-disable-line no-console
    } else {
      console.warn('New content is not compatible with current schema, ignoring it');
    }
  }

  getEditorCommands(): EditorCommands {
    const hasInlineStyle: EditorCommands['hasInlineStyle'] = style => {
      const resolvedStyles = {
        bold: this.services.content.resolve(resolversById[RESOLVERS_IDS.IS_TEXT_CONTAINS_BOLD]),
        italic: this.services.content.resolve(resolversById[RESOLVERS_IDS.IS_TEXT_CONTAINS_ITALIC]),
        underline: this.services.content.resolve(
          resolversById[RESOLVERS_IDS.IS_TEXT_CONTAINS_UNDERLINE]
        ),
        spoiler: this.services.content.resolve(
          resolversById[RESOLVERS_IDS.IS_TEXT_CONTAINS_SPOILER]
        ),
      };
      return resolvedStyles[style];
    };

    return {
      ...this.editorMocks,
      getContent: this.services.schema.getContent,
      getDocumentStyle: this.getDocumentStyle,
      toggleInlineStyle: inlineStyle => {
        const editorCommand = this.tiptapEditor.chain();
        const styleName = `toggle${capitalize(inlineStyle)}`;
        editorCommand[styleName]().run();
      },
      hasInlineStyle,
      insertBlock: (pluginType: string, data = {}) => {
        const type = TO_TIPTAP_TYPE[pluginType];
        let id = '';
        if (type) {
          const { content, ..._attrs } = data;
          id = data.id || generateId();
          const attrs = { id, ...flatComponentState(_attrs) };
          this.tiptapEditor.chain().focus().insertContent([{ type, attrs, content }]).run();
        } else {
          console.error(`No such plugin type ${pluginType}`);
        }
        return id;
      },
      insertBlockWithBlankLines: (
        pluginType: string,
        data = {},
        settings = { updateSelection: true }
      ) => {
        const type = TO_TIPTAP_TYPE[pluginType];
        let id = '';
        if (type) {
          const { content, ..._attrs } = data;
          id = data.id || generateId();
          const attrs = { id, ...flatComponentState(_attrs) };
          const { updateSelection } = settings;
          this.tiptapEditor
            .chain()
            .focus()
            .insertContent([
              { type: 'PARAGRAPH', attrs: { id: generateId() } },
              { type, attrs, content },
              { type: 'PARAGRAPH', attrs: { id: generateId() } },
            ])
            .command(({ commands, dispatch, tr }) => {
              if (updateSelection) {
                const nodeWithPos = findNodeById(tr, id)[0] || {};
                if (dispatch && nodeWithPos) {
                  return dispatch(commands.setNodeSelection(nodeWithPos.pos as number));
                }
              }
              return false;
            })
            .run();
        } else {
          console.error(`No such plugin type ${pluginType}`);
        }
        return id;
      },
      deleteBlock: nodeId => {
        return this.tiptapEditor.commands.deleteNode(nodeId);
      },
      setBlock: (nodeId, pluginType, data) => {
        return this.tiptapEditor.commands.setNodeAttrsById(nodeId, flatComponentState(data));
      },
      updateBlock: (nodeId, pluginType, data) => {
        return this.tiptapEditor.commands.updateNodeAttrsById(nodeId, flatComponentState(data));
      },
      getColor: (colorType: ColorType) =>
        this.services.content.resolve(
          resolversById[
            RESOLVERS_IDS[
              colorType === 'ricos-text-color'
                ? 'GET_TEXT_COLOR_IN_SELECTION'
                : 'GET_HIGHLIGHT_COLOR_IN_SELECTION'
            ]
          ]
        ),
      getSelection: () => {
        const {
          state: {
            doc,
            selection: { from, to },
          },
        } = this.tiptapEditor;

        const selectedNodes: ProseMirrorNode[] = [];
        doc.nodesBetween(from, to, node => {
          selectedNodes.push(node);
        });
        const hasSelectionNodes = selectedNodes.length > 0;

        return {
          isFocused: this.tiptapEditor.isFocused,
          isCollapsed: this.tiptapEditor.state.selection.empty,
          startKey: hasSelectionNodes ? selectedNodes[0].attrs.id : '',
          endKey: hasSelectionNodes ? selectedNodes[selectedNodes.length - 1].attrs.id : '',
          textContent: doc.cut(from, to).textContent,
          selectedNodes,
        };
      },

      insertDecoration: (type: string, data, settings) => {
        const decorationCommandMap = {
          [RICOS_LINK_TYPE]: data => ({ command: 'setLink', args: { link: data } }),
          [RICOS_ANCHOR_TYPE]: data => ({ command: 'setAnchor', args: data }),
          [RICOS_TEXT_COLOR_TYPE]: data => ({ command: 'setColor', args: data.color }),
          [RICOS_TEXT_HIGHLIGHT_TYPE]: data => ({ command: 'setHighlight', args: data.color }),
          [RICOS_MENTION_TYPE]: data => ({ command: 'insertMention', args: data.mention }),
          [RICOS_INDENT_TYPE]: () => ({ command: 'indent', args: undefined }),
          [RICOS_FONT_SIZE_TYPE]: data => ({
            command: 'setFontSize',
            args: parseInt(data.fontSize, 10),
          }),
          [RICOS_LINE_SPACING_TYPE]: data => ({ command: 'setLineSpacings', args: data.data }),
        };
        if (decorationCommandMap[type]) {
          const { command, args } = decorationCommandMap[type](data);
          const editorCommand = settings?.shouldFocus
            ? this.tiptapEditor.chain().focus()
            : this.tiptapEditor.chain();
          editorCommand[command](args).run();
        } else {
          console.error(`${type} decoration not supported`);
        }
      },
      getFontSize: () => {
        return this.services.content.resolve(
          resolversById[RESOLVERS_IDS.GET_FONT_SIZE_IN_SELECTION]
        );
      },
      hasLinkInSelection: (options?: { inclusive?: boolean }) => {
        const {
          state: {
            doc,
            selection: { from, to },
          },
        } = this.tiptapEditor;

        // When 'inclusive' is set to true, the selection expands to include the previous node.
        // This resolves an issue where the cursor placed at the end of a link was returning an
        // empty text node instead of including the link in the selection.
        const selectionFrom = options?.inclusive ? from - 1 : from;

        const marks: Record<string, boolean> = {};
        doc.nodesBetween(selectionFrom, to, node => {
          node.marks.forEach(({ type: { name } }) => {
            marks[name] = true;
          });
        });
        return !!(marks[Decoration_Type.LINK] || marks[Decoration_Type.ANCHOR]);
      },

      getLinkDataInSelection: (options?: { inclusive?: boolean }) => {
        const {
          state: {
            doc,
            selection: { from, to },
          },
        } = this.tiptapEditor;

        // When 'inclusive' is set to true, the selection expands to include the previous node.
        // This resolves an issue where the cursor placed at the end of a link was returning an
        // empty text node instead of including the link in the selection.
        const selectionFrom = options?.inclusive ? from - 1 : from;

        let link;
        doc.nodesBetween(selectionFrom, to, node => {
          node.marks.forEach(mark => {
            const {
              type: { name },
            } = mark;
            if (name === Decoration_Type.LINK) {
              link = mark.attrs.link;
            } else if (name === Decoration_Type.ANCHOR) {
              link = { anchor: mark.attrs.anchor };
            }
          });
        });
        return link;
      },
      setBlockType: type => {
        const blockTypeCommandMap = {
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          [UNSTYLED]: () => this.tiptapEditor.chain().focus().setParagraph().run(),
          [HEADER_BLOCK.ONE]: () =>
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            this.tiptapEditor.chain().focus().toggleHeading({ level: 1 }).run(),
          [HEADER_BLOCK.TWO]: () =>
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            this.tiptapEditor.chain().focus().toggleHeading({ level: 2 }).run(),
          [HEADER_BLOCK.THREE]: () =>
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            this.tiptapEditor.chain().focus().toggleHeading({ level: 3 }).run(),
          [HEADER_BLOCK.FOUR]: () =>
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            this.tiptapEditor.chain().focus().toggleHeading({ level: 4 }).run(),
          [HEADER_BLOCK.FIVE]: () =>
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            this.tiptapEditor.chain().focus().toggleHeading({ level: 5 }).run(),
          [HEADER_BLOCK.SIX]: () =>
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            this.tiptapEditor.chain().focus().toggleHeading({ level: 6 }).run(),
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          [BLOCKQUOTE]: () => this.tiptapEditor.commands.toggleBlockquote(),
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          [CODE_BLOCK_TYPE]: () => this.tiptapEditor.commands.toggleCodeBlock(),
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          [BULLET_LIST_TYPE]: () => this.tiptapEditor.commands.toggleBulletList(),
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          [NUMBERED_LIST_TYPE]: () => this.tiptapEditor.commands.toggleOrderedList(),
        };
        const currentSetBlockTypeCommand = blockTypeCommandMap[type];
        if (currentSetBlockTypeCommand) {
          currentSetBlockTypeCommand();
        } else {
          throw new Error(`${type} block type not supported`);
        }
      },
      // What the hell is block type lol. Is like, collapsible list not a block type?
      isBlockTypeSelected: type => {
        const blockTypeActiveCommandMap = {
          [UNSTYLED]: () => this.tiptapEditor.isActive('unstyled'),
          // This is the ONLY correct way to check if a node/mark is active
          // Node_Type shouldn't be used BUT its better than nothing
          [HEADER_BLOCK.ONE]: () => this.tiptapEditor.isActive(Node_Type.HEADING, { level: 1 }),
          [HEADER_BLOCK.TWO]: () => this.tiptapEditor.isActive(Node_Type.HEADING, { level: 2 }),
          [HEADER_BLOCK.THREE]: () => this.tiptapEditor.isActive(Node_Type.HEADING, { level: 3 }),
          [HEADER_BLOCK.FOUR]: () => this.tiptapEditor.isActive(Node_Type.HEADING, { level: 4 }),
          [HEADER_BLOCK.FIVE]: () => this.tiptapEditor.isActive(Node_Type.HEADING, { level: 5 }),
          [HEADER_BLOCK.SIX]: () => this.tiptapEditor.isActive(Node_Type.HEADING, { level: 6 }),
          [CODE_BLOCK_TYPE]: () => this.tiptapEditor.isActive(Node_Type.CODE_BLOCK),
          [BLOCKQUOTE]: () => this.tiptapEditor.isActive(Node_Type.BLOCKQUOTE),
          [NUMBERED_LIST_TYPE]: () => this.tiptapEditor.isActive(Node_Type.ORDERED_LIST),
          [BULLET_LIST_TYPE]: () => this.tiptapEditor.isActive(Node_Type.BULLETED_LIST),
        };
        const currentBlockTypeActiveCommand = blockTypeActiveCommandMap[type];

        if (currentBlockTypeActiveCommand) {
          return currentBlockTypeActiveCommand();
        } else {
          throw new Error(`${type} block type not supported`);
        }
      },
      deleteDecoration: (type: string) => {
        const deleteDecorationCommandMap = {
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          [RICOS_LINK_TYPE]: () => this.tiptapEditor.commands.unsetLink(),
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          [RICOS_ANCHOR_TYPE]: () => this.tiptapEditor.commands.unsetAnchor(),
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          [RICOS_INDENT_TYPE]: () => this.tiptapEditor.commands.outdent(),
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          [RICOS_TEXT_COLOR_TYPE]: () => this.tiptapEditor.commands.unsetColor(),
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          [RICOS_TEXT_HIGHLIGHT_TYPE]: () => this.tiptapEditor.commands.unsetHighlight(),
        };
        if (deleteDecorationCommandMap[type]) {
          deleteDecorationCommandMap[type]();
        } else {
          console.error(`delete ${type} decoration type not supported`);
        }
      },
      setTextAlignment: (alignment: TextAlignment) => {
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        this.tiptapEditor.chain().focus().setTextAlign(alignment).run();
      },
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      undo: () => this.tiptapEditor.chain().focus().undo().run(),
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      redo: () => this.tiptapEditor.chain().focus().redo().run(),
      insertText: text =>
        this.tiptapEditor
          .chain()
          .focus()
          .command(({ tr }) => {
            tr.insertText(text);
            return true;
          })
          .run(),

      getAllBlocksKeys: () => {
        const keys: string[] = [];
        this.tiptapEditor.state.doc.descendants((node: ProseMirrorNode) => {
          keys.push(node.attrs.id);
        });

        return keys;
      },
      getBlockComponentData: id => {
        const nodesWithPos = findNodeById(this.tiptapEditor.state.tr, id);
        if (nodesWithPos[0]) {
          const { node } = nodesWithPos[0];
          return node.attrs;
        } else {
          console.error('Failed to find node and return its data');
        }
      },

      resetDocumentStyleByNodeType: nodeType => {
        this.tiptapEditor
          .chain()
          .focus()
          .unsetAllMarks()
          .resetDocumentStyleByNodeType(nodeType)
          .run();
      },
      updateDocumentStyle: _documentStyles => {
        this.tiptapEditor.commands.updateDocumentStyleBySelectedNode();
      },
      clearSelectedBlocksInlineStyles: () => {
        this.tiptapEditor.commands.unsetAllMarks();
      },

      getCurrentHeading: () => {
        return this.services.content.resolve(resolversById[RESOLVERS_IDS.GET_HEADING_IN_SELECTION]);
      },
      triggerDecoration: type => {
        const triggerDecorationsMap = {
          [RICOS_MENTION_TYPE]: () =>
            this.tiptapEditor
              .chain()
              .focus()
              .command(({ tr }) => {
                tr.insertText(' @');
                return true;
              })
              .run(),
        };
        triggerDecorationsMap[type]?.();
      },
      softNewLine: () => {
        //@ts-ignore
        this.tiptapEditor.commands.setHardBreak();
      },
      clearEditorContent: (triggerUpdate = false) => {
        this.tiptapEditor.commands.clearContent(triggerUpdate);
      },
    };
  }

  editorMocks = {
    getAnchorableBlocks: () => ({
      anchorableBlocks: [],
      pluginsIncluded: [],
    }),
    getTextAlignment: (): TextAlignment =>
      this.services.content.resolve(resolversById[RESOLVERS_IDS.GET_ALIGNMENT_IN_SELECTION]),
    isBlockTypeSelected: () => false,
    isUndoStackEmpty: () =>
      this.services.content.resolve(resolversById[RESOLVERS_IDS.IS_UNDO_STACK_EMPTY]),
    isRedoStackEmpty: () =>
      this.services.content.resolve(resolversById[RESOLVERS_IDS.IS_REDO_STACK_EMPTY]),
    getSelectedData: () => 'blah',
    getPluginsList: () => [],
    scrollToBlock: _nodeId => {},
    isBlockInContent: _nodeId => false,
    toggleBlockOverlay: _nodeId => false,
    getBlockSpacing: () => {
      const lineHeight = this.services.content.resolve(
        resolversById[RESOLVERS_IDS.GET_LINE_SPACING_IN_SELECTION]
      );
      const paddingTop = this.services.content.resolve(
        resolversById[RESOLVERS_IDS.GET_LINE_SPACING_BEFORE_SELECTION]
      );
      const paddingBottom = this.services.content.resolve(
        resolversById[RESOLVERS_IDS.GET_LINE_SPACING_AFTER_SELECTION]
      );
      return {
        'line-height': lineHeight,
        'padding-top': paddingTop,
        'padding-bottom': paddingBottom,
      };
    },
    saveEditorState: () => {},
    loadEditorState: () => {},
    saveSelectionState: () => {},
    loadSelectionState: () => {},
    setBlockType: () => {},
    _setSelection: () => {},
    getAnchorBlockInlineStyles: () => {
      return {};
    },
    getInlineStylesInSelection: () => {
      const {
        state: {
          doc,
          selection: { from, to },
        },
      } = this.tiptapEditor;

      let node: Node | undefined;
      doc.nodesBetween(from, to, currNode => {
        if (
          [Node_Type.PARAGRAPH as string, Node_Type.HEADING as string].includes(
            currNode?.type?.name
          )
        ) {
          node = fromTiptapNode({ ...currNode.toJSON(), type: currNode?.type?.name });
          return false;
        }
      });
      return node ? convertInlineStylesToCSS(node) : {};
    },
    getWiredFontStyles: () => {
      const container = this.getContainer();
      const styles = window.getComputedStyle(container);

      return Object.entries(DOC_STYLE_TYPES).reduce((acc, [key, docType]) => {
        const toPropertyValue = (property: string) => ({
          [property]: styles.getPropertyValue(`--ricos-custom-${key.toLowerCase()}-${property}`),
        });

        return {
          ...acc,
          [docType]: {
            ...toPropertyValue('font-size'),
            ...toPropertyValue('font-family'),
            ...toPropertyValue('color'),
            ...toPropertyValue('background-color'),
          },
        };
      }, {});
    },
    isAtomicBlockInSelection: () => false,
    isTextBlockInSelection: () => true,
    getAnchorBlockType: () => 'paragraph',
    focus: (focusPosition?: FocusPosition) => this.focus(focusPosition),
  };
}

const flatComponentState = data => {
  const { componentState, ...rest } = data;
  return { ...(rest || {}), ...(componentState || {}) };
};
